import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { getMyPermissions } from "../services/permissions";

export const PermissionContext = createContext(null);

// Loads what the signed-in user may do (GET permissions/me) once and shares it with the layout: the menu, the pages
// and the buttons ask `can("customer.edit")`. A key list means "any of them". Full access can do everything.
// While the permissions load nothing is allowed (`loading` is true, pages wait); if they cannot be loaded nothing is
// allowed either (`error` is set) - the API checks the same permissions, so hiding is only for convenience.
const PermissionProvider = ({ children }) => {
  const [state, setState] = useState({ loading: true, error: false, all: false, keys: new Set() });

  const load = useCallback(() => {
    let ignore = false;
    setState((current) => ({ ...current, loading: true, error: false }));
    getMyPermissions()
      .then(({ all, permissions }) => {
        if (!ignore) setState({ loading: false, error: false, all, keys: new Set(permissions) });
      })
      .catch(() => {
        if (!ignore) setState({ loading: false, error: true, all: false, keys: new Set() });
      });
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  const value = useMemo(() => {
    const can = (key) => {
      if (!key) return true; // nothing is asked for
      if (Array.isArray(key)) return key.length === 0 || key.some(can);
      return state.all || state.keys.has(key);
    };
    return { loading: state.loading, error: state.error, all: state.all, can, reload: load };
  }, [state, load]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export default PermissionProvider;
